import React, { FC, Suspense, useCallback, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Checkbox } from 'src/components/widgets/checkbox';
import ContentLoader from 'react-content-loader';
import theme from 'src/shared/theme';
import { ZSwitch } from 'src/components/widgets/zswitch';
import { T } from 'src/shared/helpers';
import { useRelayStore } from 'src/relay/environment';
import type { ProfanityRuleFullData } from '../common/types';
import { TOOL_DEFINITIONS, PROFANITY_CATEGORIES, SUBCATEGORY_TO_CATEGORY, PROFANITY_LANGS } from '../common/constants';
import type { profanityRuleEditor_ProfanityFilterRuleDetailQuery } from './__generated__/profanityRuleEditor_ProfanityFilterRuleDetailQuery.graphql';
import type { profanityRuleEditor_UpdateProfanityFilterRuleMutation } from './__generated__/profanityRuleEditor_UpdateProfanityFilterRuleMutation.graphql';
import { StackedBreadcrumb, StackedBody, SectionCard, SectionCardTitle, CheckboxGrid, SharedWarningCallout, SelectAllLink } from '../common/styled';

export interface ProfanityRuleEditorProps {
    itemId: string;
    productId: string;
    toolKey: string;
    onBack: () => void;
    onSave: () => void;
    saveRef: React.MutableRefObject<(() => Promise<void>) | null>;
}

const profanityFilterRuleDetailQuery = graphql`
    query profanityRuleEditor_ProfanityFilterRuleDetailQuery($id: ID!) {
        profanityFilterRule(id: $id) {
            id
            profanityFilterRuleID
            name
            enableTextFilter
            enableImageModeration
            langDetection
            lang
            moderationCategories {
                moderationCategoryID
                moderationCategoryTypeID
                value
                name
            }
            products {
                productID
            }
            printTypes {
                printTypeID
            }
        }
    }
`;

const ProfanityRuleEditorContent: FC<ProfanityRuleEditorProps> = ({ itemId, productId, toolKey, onBack, onSave, saveRef }) => {
    const toolName = TOOL_DEFINITIONS.find((tool) => tool.key === toolKey)?.name || '';
    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query profanityRuleEditor_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const json = useLazyLoadQuery<profanityRuleEditor_ProfanityFilterRuleDetailQuery>(profanityFilterRuleDetailQuery, { id: itemId });
    const rule = json?.profanityFilterRule;
    const ruleName = rule?.name || '';
    const assignedProductCount = rule?.products?.length;

    const invalidate = useRelayStore((s) => s.invalidateRelayStore);
    const ruleRef = useRef<ProfanityRuleFullData | null>(rule as ProfanityRuleFullData | null);
    const [saving, setSaving] = useState(false);
    const [enableTextFilter, setEnableTextFilter] = useState(() => rule?.enableTextFilter ?? false);
    const [enableImageModeration, setEnableImageModeration] = useState(() => rule?.enableImageModeration ?? false);
    const [activeCategories, setActiveCategories] = useState<Set<string>>(() => {
        const cats = new Set<string>();
        (rule?.moderationCategories || []).forEach((moderationCategory) => {
            const parent = moderationCategory.name ? SUBCATEGORY_TO_CATEGORY[moderationCategory.name] : undefined;
            if (parent) cats.add(parent);
        });
        return cats;
    });
    const [activeLangs, setActiveLangs] = useState<Set<string>>(() => {
        const langs = new Set<string>();
        (rule?.lang || '')
            .split(',')
            .filter(Boolean)
            .forEach((lang: string) => langs.add(lang.trim()));
        return langs;
    });

    const [commitUpdateProfanity] = useMutation<profanityRuleEditor_UpdateProfanityFilterRuleMutation>(graphql`
        mutation profanityRuleEditor_UpdateProfanityFilterRuleMutation($input: ProfanityFilterRuleInput!) {
            updateProfanityFilterRule(input: $input) {
                profanityFilterRule {
                    id
                }
            }
        }
    `);

    const toggleCategory = (catName: string) => {
        setActiveCategories((prev) => {
            const next = new Set(prev);
            if (next.has(catName)) next.delete(catName);
            else next.add(catName);
            return next;
        });
    };

    const toggleLang = (code: string) => {
        setActiveLangs((prev) => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code);
            else next.add(code);
            return next;
        });
    };

    const handleSave = useCallback(async () => {
        if (!ruleRef.current) return;
        setSaving(true);
        try {
            // Build moderationCategories from active parent categories
            const moderationCategories: { moderationCategoryID: number; moderationCategoryTypeID: number; value: string; name: string }[] = [];
            PROFANITY_CATEGORIES.forEach((category) => {
                if (activeCategories.has(category.name)) {
                    category.subcategories.forEach((sub) => {
                        // Preserve existing value if present
                        const existing = ruleRef.current!.moderationCategories.find(
                            (moderationCategory) => moderationCategory.moderationCategoryTypeID === sub.id
                        );
                        moderationCategories.push({
                            moderationCategoryID: existing?.moderationCategoryID ?? 0,
                            moderationCategoryTypeID: sub.id,
                            value: existing?.value ?? sub.defaultValue.toString(),
                            name: sub.name
                        });
                    });
                }
            });

            const input = {
                id: ruleRef.current.id,
                name: ruleRef.current.name,
                apiKey: '',
                lang: Array.from(activeLangs).join(','),
                enableTextFilter,
                enableImageModeration,
                langDetection: ruleRef.current.langDetection,
                moderationCategories,
                products: ruleRef.current.products.map((p) => p.productID),
                printTypes: ruleRef.current.printTypes.map((p) => p.printTypeID)
            };

            await new Promise<void>((resolve, reject) => {
                commitUpdateProfanity({
                    variables: { input },
                    updater: (store) => store.invalidateStore(),
                    onCompleted: () => {
                        invalidate();
                        onSave();
                        setSaving(false);
                        resolve();
                    },
                    onError: (error) => {
                        setSaving(false);
                        reject(error);
                    }
                });
            });
        } catch {
            /* ignore */
            setSaving(false);
        }
    }, [activeCategories, activeLangs, enableTextFilter, enableImageModeration, onSave, commitUpdateProfanity]);

    // Expose save handler to parent
    saveRef.current = handleSave;

    return (
        <>
            <StackedBreadcrumb>
                <span onClick={onBack}>{productName}</span> / {toolName} / {ruleName}
            </StackedBreadcrumb>
            <StackedBody>
                {assignedProductCount != null && assignedProductCount > 1 && (
                    <SharedWarningCallout>
                        {T._('This rule is assigned to', 'Admin')} <strong>{assignedProductCount}</strong>{' '}
                        {T._('products. Any changes will apply to all of them.', 'Admin')}
                    </SharedWarningCallout>
                )}
                <SectionCard>
                    <SectionCardTitle>
                        {T._('Text Filter', 'Admin')}
                        <ZSwitch checked={enableTextFilter} onChange={() => setEnableTextFilter(!enableTextFilter)} />
                    </SectionCardTitle>
                </SectionCard>

                <SectionCard>
                    <SectionCardTitle>
                        {T._('Image Moderator', 'Admin')}
                        <ZSwitch checked={enableImageModeration} onChange={() => setEnableImageModeration(!enableImageModeration)} />
                    </SectionCardTitle>
                    {enableImageModeration && (
                        <>
                            <div>
                                <SelectAllLink
                                    onClick={() => {
                                        const allSelected = PROFANITY_CATEGORIES.every((category) => activeCategories.has(category.name));
                                        setActiveCategories(allSelected ? new Set() : new Set(PROFANITY_CATEGORIES.map((category) => category.name)));
                                    }}
                                >
                                    {T._('Select all', 'Admin')}
                                </SelectAllLink>
                            </div>
                            <CheckboxGrid>
                                {PROFANITY_CATEGORIES.map((category) => (
                                    <Checkbox
                                        key={category.name}
                                        label={category.label}
                                        checked={activeCategories.has(category.name)}
                                        onChange={(checked) => {
                                            if (checked !== activeCategories.has(category.name)) toggleCategory(category.name);
                                        }}
                                    />
                                ))}
                            </CheckboxGrid>
                        </>
                    )}
                </SectionCard>

                <SectionCard>
                    <SectionCardTitle>{T._('Languages', 'Admin')}</SectionCardTitle>
                    <div>
                        <SelectAllLink
                            onClick={() => {
                                const allSelected = PROFANITY_LANGS.every((lang) => activeLangs.has(lang.code));
                                setActiveLangs(allSelected ? new Set() : new Set(PROFANITY_LANGS.map((lang) => lang.code)));
                            }}
                        >
                            {T._('Select all', 'Admin')}
                        </SelectAllLink>
                    </div>
                    <CheckboxGrid>
                        {PROFANITY_LANGS.map((lang) => (
                            <Checkbox
                                key={lang.code}
                                label={lang.label}
                                checked={activeLangs.has(lang.code)}
                                onChange={(checked) => {
                                    if (checked !== activeLangs.has(lang.code)) toggleLang(lang.code);
                                }}
                            />
                        ))}
                    </CheckboxGrid>
                </SectionCard>
            </StackedBody>
        </>
    );
};

export const ProfanityRuleEditor: FC<ProfanityRuleEditorProps> = (props) => {
    return (
        <Suspense
            fallback={
                <>
                    <StackedBreadcrumb>
                        <span onClick={props.onBack}>{T._('Loading...', 'Admin')}</span>
                    </StackedBreadcrumb>
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='60'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='48' rx='8' ry='8' width='60%' height='16' />
                        </ContentLoader>
                    </StackedBody>
                </>
            }
        >
            <ProfanityRuleEditorContent {...props} />
        </Suspense>
    );
};
